import Config from '@/config';
import { BrokerageHttpClient } from '../../utils/HttpClient';

const getAxios = () => BrokerageHttpClient;

const ironsides_url = Config.IRONSIDES_URL;
const hornet_url = Config.HORNET_URL;
const manifests_url = Config.MANIFESTS_URL;

export const Api = {
  ////////////////////////
  // Admins
  ////////////////////////
  login(payload) {
    return getAxios().post(`${ironsides_url}admin_auth/sign_in`, payload);
  },

  ////////////////////////
  // Admins
  ////////////////////////
  loadAdmins() {
    return getAxios().get(`${ironsides_url}admins`);
  },

  loadAdmin(id) {
    return getAxios().get(`${ironsides_url}admins/${id}`);
  },

  saveAdmin(id, payload) {
    if (id) {
      return getAxios().put(`${ironsides_url}admins/${id}`, payload);
    } else {
      return getAxios().post(`${ironsides_url}admins`, payload);
    }
  },

  deleteAdmin(id) {
    return getAxios().delete(`${ironsides_url}admins/${id}`);
  },

  ////////////////////////
  // Agencies
  ////////////////////////
  createAgency(payload) {
    return getAxios().post(`${ironsides_url}agencies`, payload);
  },

  deleteAgencyForm(guid) {
    return getAxios().delete(`${ironsides_url}agency_forms/${guid}`);
  },

  loadAgencies(params) {
    return getAxios().get(`${ironsides_url}agencies`, { params });
  },

  saveDemoAgency(payload) {
    return getAxios().post(`${ironsides_url}agencies`, payload);
  },

  loadAgency(id) {
    return getAxios().get(`${ironsides_url}agencies/${id}`);
  },

  loadAgencyBillingProfile(guid) {
    return getAxios().get(`${ironsides_url}agency_billing_profiles/${guid}`);
  },

  updateAgencyBillingProfile(guid, payload) {
    return getAxios().put(`${ironsides_url}agency_billing_profiles/${guid}`, payload);
  },

  // update stripe billing info
  updateStripeBillingInfo(id, data) {
    return getAxios().put(`${ironsides_url}stripe_customers/${id}`, data);
  },

  loadFirelightProducts(agency_id, activity) {
    return getAxios().get(
      `${ironsides_url}carrier_products/firelight_products?agency_id=${agency_id}&activity=${activity}`
    );
  },

  loadLifeCarriers(beta) {
    if (beta) {
      return getAxios().get(`${hornet_url}v2/carriers/beta_life_quote_carriers?all_data=true`);
    } else {
      return getAxios().get(`${hornet_url}v2/carriers/live_life_quote_carriers?all_data=true`);
    }
  },

  loadAnnuityCarriers() {
    return getAxios().get(`${hornet_url}v2/carriers/live_annuity_quote_carriers?all_data=true`);
  },

  loadAgencySpecificCarriers() {
    return getAxios().get(`${hornet_url}v2/carriers?all_data=true&is_agency_specific=true`);
  },

  loadAgencyForm(guid) {
    return getAxios().get(`${ironsides_url}agency_forms/${guid}`);
  },

  loadAgencyForms(guid) {
    return getAxios().get(`${ironsides_url}agency_forms?agency_guid=${guid}`);
  },

  loadAgencyMobileApps(id) {
    return getAxios().get(`${ironsides_url}mobile_apps?agency_id=${id}`);
  },

  ////////////////////
  // Annuity Quoter
  ////////////////////
  loadAnnuityQuoter(id) {
    return getAxios().get(`${ironsides_url}annuity_quoters/${id}`);
  },

  loadAnnuityQuoters(id) {
    return getAxios().get(`${ironsides_url}annuity_quoters?agency_id=${id}`);
  },

  updateAnnuityQuoter(token, payload) {
    if (payload.carrier_ids) {
      payload.carrier_ids = [...new Set(payload.carrier_ids.flat(Infinity))];
    }
    if (payload.product_ids) {
      payload.product_ids = [...new Set(payload.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}annuity_quoters/${token}`, payload);
  },

  loadAnnuityQuoterThemeOptions(id) {
    return getAxios().get(`${ironsides_url}annuity_quoter_theme_options/${id}`);
  },

  updateAnnuityQuoterThemeOptions(id, payload) {
    return getAxios().put(`${ironsides_url}annuity_quoter_theme_options/${id}`, payload);
  },

  loadAnnuityQuoterCustomActions(token) {
    return getAxios().get(
      `${ironsides_url}annuity_quoter_custom_actions?annuity_quoter_token=${token}`
    );
  },

  saveAnnuityQuoterCustomAction(id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['icon'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        // For arrays containing objects, stringify each object
        value.forEach(v => {
          if (typeof v === 'object' && v !== null) {
            form_data.append(`${key}[]`, JSON.stringify(v));
          } else {
            form_data.append(`${key}[]`, v);
          }
        });
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(k => {
          form_data.append(`${key}[${k}]`, value[k]);
        });
      } else {
        form_data.append(key, value);
      }
    });

    if (id) {
      return getAxios().put(`${ironsides_url}annuity_quoter_custom_actions/${id}`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      return getAxios().post(`${ironsides_url}annuity_quoter_custom_actions`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
  },

  deleteAnnuityQuoterCustomAction(id) {
    return getAxios().delete(`${ironsides_url}annuity_quoter_custom_actions/${id}`);
  },

  //Universal Quoter
  loadUniversalQuoter(id) {
    return getAxios().get(`${ironsides_url}universal_quoters/${id}`);
  },
  updateUniversalQuoter(token, payload) {
    if (payload.carrier_ids) {
      payload.carrier_ids = [...new Set(payload.carrier_ids.flat(Infinity))];
    }
    if (payload.product_ids) {
      payload.product_ids = [...new Set(payload.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}universal_quoters/${token}`, payload);
  },
  ////////////////////
  // API Client
  ////////////////////
  loadApiClient(app_id) {
    return getAxios().get(`${ironsides_url}api_clients/${app_id}`);
  },

  loadApiClients(id) {
    return getAxios().get(`${ironsides_url}api_clients?agency_id=${id}`);
  },
  loadViveCarriers() {
    return getAxios().get(`${hornet_url}v2/carriers/live_term_quote_carriers?all_data=true`);
  },
  loadViveProducts(carrier_id) {
    return getAxios().get(`${hornet_url}v2/products/products_ext?carrier_id[]=${carrier_id}`);
  },
  updateViveQuoter(id, input_fields) {
    if (input_fields.carrier_ids) {
      input_fields.carrier_ids = [...new Set(input_fields.carrier_ids.flat(Infinity))];
    }
    if (input_fields.product_ids) {
      input_fields.product_ids = [...new Set(input_fields.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}api_clients/${id}`, input_fields);
  },

  ////////////////////
  // ALQ
  ////////////////////
  loadBgaQuoter(id) {
    return getAxios().get(`${ironsides_url}bga_quoters/${id}`);
  },

  loadBgaQuoters(id) {
    return getAxios().get(`${ironsides_url}bga_quoters?agency_id=${id}`);
  },

  updateBgaQuoter(token, payload) {
    if (payload.carrier_ids) {
      payload.carrier_ids = [...new Set(payload.carrier_ids.flat(Infinity))];
    }
    if (payload.product_ids) {
      payload.product_ids = [...new Set(payload.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}bga_quoters/${token}`, payload);
  },

  loadBgaQuoterThemeOptions(bga_quoter_theme_options_id) {
    return getAxios().get(
      `${ironsides_url}bga_quoter_theme_options/${bga_quoter_theme_options_id}`
    );
  },

  updateThemeOptions(bga_quoter_theme_options_id, payload) {
    return getAxios().put(
      `${ironsides_url}bga_quoter_theme_options/${bga_quoter_theme_options_id}`,
      payload
    );
  },

  loadBgaQuoterCustomActions(bga_quoter_token) {
    return getAxios().get(
      `${ironsides_url}bga_quoter_custom_actions?bga_quoter_token=${bga_quoter_token}`
    );
  },

  saveBgaQuoterCustomAction(id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['icon'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(k => {
          form_data.append(`${key}[${k}]`, value[k]);
        });
      } else {
        form_data.append(key, value);
      }
    });

    if (id) {
      return getAxios().put(`${ironsides_url}bga_quoter_custom_actions/${id}`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      return getAxios().post(`${ironsides_url}bga_quoter_custom_actions`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
  },

  deleteBgaCustomAction(custom_action_id) {
    return getAxios().delete(`${ironsides_url}bga_quoter_custom_actions/${custom_action_id}`);
  },

  loadFormEngines(id) {
    return getAxios().get(`${ironsides_url}form_engines?agency_id=${id}`);
  },

  loadFormTags() {
    return getAxios().get(`${manifests_url}/form_tags/`);
  },

  loadLead(id) {
    return getAxios().get(`${ironsides_url}leads/${id}`);
  },

  loadLeads(params) {
    return getAxios().get(`${ironsides_url}leads`, { params });
  },

  loadQuote(id) {
    return getAxios().get(`${hornet_url}v2/quotes/${id}`);
  },

  updateLeads(id, payload) {
    return getAxios().put(`${ironsides_url}leads/${id}`, payload);
  },

  loadLtcQuoters(id) {
    return getAxios().get(`${ironsides_url}ltc_quoters?agency_id=${id}`);
  },

  loadWebsite(guid) {
    return getAxios().get(`${ironsides_url}websites/${guid}`);
  },

  loadWebsites(id) {
    return getAxios().get(`${ironsides_url}websites?agency_id=${id}`);
  },

  loadAgencyNotificationTriggers(agency_id) {
    return getAxios().get(`${ironsides_url}notification_triggers?agency_id=${agency_id}`);
  },

  saveAgencyNotificationTrigger(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}notification_triggers/${id}`, payload)
      : getAxios().post(`${ironsides_url}notification_triggers`, payload);
  },

  loadIxnProducts(params) {
    return getAxios().get(`${ironsides_url}products`, { params });
  },

  updateWebsite(guid, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['contract'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return getAxios().put(`${ironsides_url}websites/${guid}`, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  createNewProduct(type, payload) {
    return getAxios().post(`${ironsides_url}${type}`, payload);
  },

  createAgency(payload) {
    return getAxios().post(`${ironsides_url}agencies/new`, payload);
  },

  updateAgency(id, payload) {
    return getAxios().put(`${ironsides_url}agencies/${id}`, payload);
  },

  saveAgencyForm(guid, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['pdf_file'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return guid
      ? getAxios().put(`${ironsides_url}agency_forms/${guid}`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      : getAxios().post(`${ironsides_url}agency_forms`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
  },

  updateApiClient(id, payload) {
    return getAxios().put(`${ironsides_url}api_clients/${id}`, payload);
  },

  ////////////////////////
  // Members
  ////////////////////////
  loadMembers(params) {
    return getAxios().get(`${ironsides_url}members`, { params });
  },

  loadMember(id) {
    return getAxios().get(`${ironsides_url}members/${id}`);
  },

  unlockMember(id) {
    return getAxios().post(`${ironsides_url}members/${id}/unlock`);
  },

  importMembers(id, members_json) {
    return getAxios().post(`${ironsides_url}members/bulk_import`, {
      agency_id: id,
      members_json: JSON.stringify(members_json)
    });
  },

  createMember(payload) {
    return getAxios().post(`${ironsides_url}members`, payload);
  },

  updateMember(id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['image'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return getAxios().put(`${ironsides_url}members/${id}`, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteMember(id) {
    return getAxios().delete(`${ironsides_url}members/${id}`);
  },

  loadMemberAgencyRole(id) {
    return getAxios().get(`${ironsides_url}member_agency_roles/${id}`);
  },

  saveMemberAgencyRole(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}member_agency_roles/${id}`, payload)
      : getAxios().post(`${ironsides_url}member_agency_roles`, payload);
  },

  deleteMemberAgencyRole(id) {
    return getAxios().delete(`${ironsides_url}member_agency_roles/${id}`);
  },

  loadMobileDevices(params) {
    return getAxios().get(`${ironsides_url}mobile_devices`, { params });
  },

  deleteMobileDevice(id) {
    return getAxios().delete(`${ironsides_url}mobile_devices/${id}`);
  },

  loadMemberBgaQuoters(member_id, agency_id) {
    const agency = agency_id ? `&agency_id=${agency_id}` : '';

    return getAxios().get(`${ironsides_url}bga_quoters?member_id=${member_id}${agency}`);
  },

  loadMemberWebsiteQuoters(member_id, agency_id) {
    const agency = agency_id ? `&agency_id=${agency_id}` : '';

    return getAxios().get(`${ironsides_url}website_quoters?member_id=${member_id}${agency}`);
  },

  loadMemberMobileApps(member_id, agency_id) {
    const agency = agency_id ? `&agency_id=${agency_id}` : '';

    return getAxios().get(`${ironsides_url}mobile_apps?member_id=${member_id}${agency}`);
  },

  loadNotificationTriggers(member_id) {
    return getAxios().get(`${ironsides_url}notification_triggers?owner_id=${member_id}`);
  },

  loadNotificationTrigger(id) {
    return getAxios().get(`${ironsides_url}notification_triggers/${id}`);
  },

  saveNotificationTrigger(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}notification_triggers/${id}`, payload)
      : getAxios().post(`${ironsides_url}notification_triggers`, payload);
  },

  deleteNotificationTrigger(id) {
    return getAxios().delete(`${ironsides_url}notification_triggers/${id}`);
  },

  ////////////////////////
  // Agencies/Mobile Apps
  ////////////////////////
  loadAgencyMobileApp(guid) {
    return getAxios().get(`${ironsides_url}mobile_apps/${guid}`);
  },

  updateAgencyMobileApp(guid, payload) {
    return getAxios().put(`${ironsides_url}mobile_apps/${guid}`, payload);
  },

  loadAgencyMobileAppAdCampaigns(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_ad_campaigns?mobile_app_guid=${mobile_app_guid}`
    );
  },

  loadAgencyMobileAppAdCampaign(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_ad_campaigns/${guid}`);
  },

  updateAgencyMobileAppAdCampaign(id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['banner_ad_image', 'banner_ad_popup_image', 'interstitial_ad_image'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return id
      ? getAxios().put(`${ironsides_url}mobile_app_ad_campaigns/${id}`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      : getAxios().post(`${ironsides_url}mobile_app_ad_campaigns`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
  },

  deleteAgencyMobileAppAdCampaign(id) {
    return getAxios().delete(`${ironsides_url}mobile_app_ad_campaigns/${id}`);
  },

  loadAgencyMobileAppCustomAction(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_custom_actions/${guid}`);
  },

  loadAgencyMobileAppCustomActions(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_custom_actions?mobile_app_guid=${mobile_app_guid}`
    );
  },

  saveMobileAppCustomAction(guid, payload) {
    if (guid) {
      return getAxios().put(`${ironsides_url}mobile_app_custom_actions/${guid}`, payload);
    } else {
      return getAxios().post(`${ironsides_url}mobile_app_custom_actions`, payload);
    }
  },

  deleteMobileAppCustomAction(custom_action_id) {
    return getAxios().delete(`${ironsides_url}mobile_app_custom_actions/${custom_action_id}`);
  },

  // Quote Promotions
  loadAllCarriers() {
    return getAxios().get(`${hornet_url}v2/carriers`);
  },

  loadAllProducts() {
    return getAxios().get(`${hornet_url}v2/products`);
  },

  loadAgencyMobileAppQuotePromotions(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_quote_promotions?mobile_app_guid=${mobile_app_guid}`
    );
  },

  loadAgencyMobileAppQuotePromotion(promotion_guid) {
    return getAxios().get(`${ironsides_url}mobile_app_quote_promotions/${promotion_guid}`);
  },

  updateMobileAppQuotePromotion(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}mobile_app_quote_promotions/${id}`, payload)
      : getAxios().post(`${ironsides_url}mobile_app_quote_promotions`, payload);
  },

  deleteMobileAppQuotePromotion(id) {
    return getAxios().delete(`${ironsides_url}mobile_app_quote_promotions/${id}`);
  },

  loadMobileAppResourceCategory(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_resource_categories/${guid}`);
  },

  loadMobileAppResourceCategories(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_resource_categories?mobile_app_guid=${mobile_app_guid}`
    );
  },

  saveMobileAppResourceCategory(guid, payload) {
    return guid
      ? getAxios().put(`${ironsides_url}mobile_app_resource_categories/${guid}`, payload)
      : getAxios().post(`${ironsides_url}mobile_app_resource_categories`, payload);
  },

  deleteMobileAppResourceCategory(guid) {
    return getAxios().delete(`${ironsides_url}mobile_app_resource_categories/${guid}`);
  },

  loadMobileAppResourceLink(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_resource_links/${guid}`);
  },

  loadMobileAppResourceLinks(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_resource_links?mobile_app_guid=${mobile_app_guid}`
    );
  },

  saveMobileAppResourceLink(guid, payload) {
    return guid
      ? getAxios().put(`${ironsides_url}mobile_app_resource_links/${guid}`, payload)
      : getAxios().post(`${ironsides_url}mobile_app_resource_links`, payload);
  },

  deleteMobileAppResourceLink(guid) {
    return getAxios().delete(`${ironsides_url}mobile_app_resource_links/${guid}`);
  },

  // Annuity Products
  loadAllAnnuityProducts() {
    return getAxios().get(`${hornet_url}v2/products/annuity_products`);
  },

  loadAgencyMobileAppAAQQuotePromotions(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_annuity_quotes_quote_promotions?mobile_app_guid=${mobile_app_guid}`
    );
  },

  updateMobileAppAAQQuotePromotion(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}mobile_app_annuity_quotes_quote_promotions/${id}`, payload)
      : getAxios().post(`${ironsides_url}mobile_app_annuity_quotes_quote_promotions`, payload);
  },

  deleteMobileAppAAQQuotePromotion(id) {
    return getAxios().delete(`${ironsides_url}mobile_app_annuity_quotes_quote_promotions/${id}`);
  },

  loadAgencyMobileAppAAQCustomActions(mobile_app_guid) {
    return getAxios().get(
      `${ironsides_url}mobile_app_annuity_custom_actions?mobile_app_guid=${mobile_app_guid}`
    );
  },

  saveMobileAppAAQCustomAction(id, payload) {
    return id
      ? getAxios().put(`${ironsides_url}mobile_app_annuity_custom_actions/${id}`, payload)
      : getAxios().post(`${ironsides_url}mobile_app_annuity_custom_actions`, payload);
  },

  deleteMobileAppAAQCustomAction(custom_action_id) {
    return getAxios().delete(
      `${ironsides_url}mobile_app_annuity_custom_actions/${custom_action_id}`
    );
  },

  ////////////////////////
  // LTC Quoters
  ////////////////////////
  loadLtcQuoter(ltc_quoter_id) {
    return getAxios().get(`${ironsides_url}ltc_quoters/${ltc_quoter_id}`);
  },

  updateLtcQuoter(ltc_quoter_id, payload) {
    if (payload.carrier_ids) {
      payload.carrier_ids = [...new Set(payload.carrier_ids.flat(Infinity))];
    }
    if (payload.product_ids) {
      payload.product_ids = [...new Set(payload.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}ltc_quoters/${ltc_quoter_id}`, payload);
  },
  saveLtcQuoterCustomAction(ltc_quoter_id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['icon'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(k => {
          form_data.append(`${key}[${k}]`, value[k]);
        });
      } else {
        form_data.append(key, value);
      }
    });

    if (ltc_quoter_id) {
      return getAxios().put(
        `${ironsides_url}ltc_quoter_custom_actions/${ltc_quoter_id}`,
        form_data,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    } else {
      return getAxios().post(`${ironsides_url}ltc_quoter_custom_actions`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
  },
  loadLtcQuoterCustomActions(ltc_quoter_id) {
    return getAxios().get(
      `${ironsides_url}ltc_quoter_custom_actions?ltc_quoter_id=${ltc_quoter_id}`
    );
  },
  deleteLtcCustomAction(custom_action_id) {
    return getAxios().delete(`${ironsides_url}ltc_quoter_custom_actions/${custom_action_id}`);
  },

  ////////////////////////
  // Form Engines
  ////////////////////////
  loadFormEngine(guid) {
    return getAxios().get(`${ironsides_url}form_engines/${guid}`);
  },

  updateFormEngine(id, payload) {
    return getAxios().put(`${ironsides_url}form_engines/${id}`, payload);
  },

  loadFormCarriers(is_beta) {
    return is_beta
      ? getAxios().get(`${hornet_url}v2/carriers/beta_form_carriers`)
      : getAxios().get(`${hornet_url}v2/carriers/live_form_carriers`);
  },

  loadFormEngineThemeOptions(form_engine_theme_options_id) {
    return getAxios().get(
      `${ironsides_url}form_engine_theme_options/${form_engine_theme_options_id}`
    );
  },

  updateFormEngineThemeOptions(form_engine_theme_options_id, payload) {
    return getAxios().put(
      `${ironsides_url}form_engine_theme_options/${form_engine_theme_options_id}`,
      payload
    );
  },

  ////////////////////////
  // Carriers
  ////////////////////////
  loadCarrier(carrier_id) {
    return getAxios().get(`${ironsides_url}carriers/${carrier_id}`);
  },

  loadCarriers() {
    return getAxios().get(`${ironsides_url}carriers`);
  },

  saveCarrier(id, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['logo'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return id
      ? getAxios().put(`${ironsides_url}carriers/${id}`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      : getAxios().post(`${ironsides_url}carriers`, form_data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
  },

  deleteCarrier(carrier_id) {
    return getAxios().delete(`${ironsides_url}carriers/${carrier_id}`);
  },

  // Carrier products
  loadCarrierSpecificProducts(carrier_id) {
    return getAxios().get(`${ironsides_url}carrier_products?carrier_id=${carrier_id}`);
  },

  saveCarrierProduct(id, payload) {
    if (id) {
      return getAxios().put(`${ironsides_url}carrier_products/${id}`, payload);
    } else {
      return getAxios().post(`${ironsides_url}carrier_products`, payload);
    }
  },

  deleteCarrierProduct(carrier_product_id) {
    return getAxios().delete(`${ironsides_url}carrier_products/${carrier_product_id}`);
  },

  ////////////////////////
  // Mobile Apps
  ////////////////////////
  buildCreate(payload) {
    return getAxios().post(`${ironsides_url}mobile_app_builds`, payload);
  },

  buildDelete(guid) {
    return getAxios().delete(`${ironsides_url}mobile_app_builds/${guid}`);
  },

  updateBuildConfigValues(values) {
    const requests = [];

    values.forEach(value => {
      if (value.file_value) {
        const form_data = new FormData();

        form_data.append('file_value', value.file_value);

        requests.push(
          getAxios().put(`${ironsides_url}mobile_app_config_values/${value.guid}`, form_data, {
            headers: { 'Context-Type': 'multipart/form-data' }
          })
        );
      } else {
        requests.push(
          getAxios().put(`${ironsides_url}mobile_app_config_values/${value.guid}`, value)
        );
      }
    });

    return Promise.all(requests);
  },

  loadMobileAppBuilds(params) {
    return getAxios().get(`${ironsides_url}mobile_app_builds`, { params });
  },

  createConfigValue(build_guid, config_value) {
    if (config_value.file_value && typeof config_value.file_value !== 'string') {
      // Handle file upload with FormData
      const form_data = new FormData();

      // Add the file
      form_data.append('file_value', config_value.file_value);

      // Add all other properties to form_data
      Object.keys(config_value).forEach(key => {
        if (key !== 'file_value') {
          form_data.append(key, config_value[key]);
        }
      });

      return getAxios().post(`${ironsides_url}mobile_app_config_values}`, form_data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    // For non-file values, send as regular JSON
    return getAxios().post(`${ironsides_url}mobile_app_config_values`, config_value);
  },

  deleteConfigField(guid) {
    return getAxios().delete(`${ironsides_url}mobile_app_config_fields/${guid}`);
  },

  deleteConfigValue(guid) {
    return getAxios().delete(`${ironsides_url}mobile_app_config_values/${guid}`);
  },

  loadApp(guid) {
    return getAxios().get(`${ironsides_url}mobile_apps/${guid}`);
  },

  loadBuild(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_builds/${guid}`);
  },

  loadBuilds(guid, page) {
    return getAxios().get(
      `${ironsides_url}mobile_app_builds?mobile_app_guid=${guid}&page=${page}&per_page=20`
    );
  },

  loadConfigFields(params) {
    const release = params.github_release_version
      ? `?version=${params.github_release_version}`
      : '';
    const page = params.page ? `?&page=${params.page}&per_page=20` : '';
    const search_term = params.search_term ? `&search=${params.search_term}` : '';

    return getAxios().get(
      `${ironsides_url}mobile_app_config_fields${release}${page}${search_term}`
    );
  },

  loadConfigField(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_config_fields/${guid}`);
  },

  updateConfigField(guid, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['file_default'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return getAxios().put(`${ironsides_url}mobile_app_config_fields/${guid}`, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  createConfigField(payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (['file_default'].includes(key)) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else if (value !== null) {
        form_data.append(key, value);
      }
    });

    return getAxios().post(`${ironsides_url}mobile_app_config_fields`, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  loadConfigValues(guid) {
    return getAxios().get(`${ironsides_url}mobile_app_config_values?build_guid=${guid}`);
  },

  loadMobileApps(params) {
    return getAxios().get(`${ironsides_url}mobile_apps`, { params });
  },

  updateMobileApp(guid, payload) {
    const form_data = new FormData();

    Object.keys(payload).forEach(key => {
      const value = payload[key];

      if (
        [
          'background',
          'contact_us_icon',
          'default_h_screen',
          'default_portrait_1366_screen',
          'default_portrait_screen',
          'default_screen',
          'etickets_icon',
          'google_keystore',
          'icon_background',
          'icon_dpi',
          'icon_foreground',
          'icon_notification_default',
          'icon',
          'ios_distribution_certificate',
          'ios_provisioning_profile',
          'ios_push_certificate',
          'life_quotes_icon',
          'links_icon',
          'logo',
          'logout_icon',
          'menu_logo',
          'my_sales_rep_icon',
          'needs_calculator_icon',
          'notifications_icon',
          'privacy_policy_icon',
          'tos_icon',
          'uwp_icon',
          'videos_icon',
          'website_icon',
          'webview1_icon',
          'webview2_icon',
          'webview3_icon',
          'webview4_icon',
          'webview5_icon',
          'webview6_icon'
        ].includes(key)
      ) {
        if (payload[key]) {
          form_data.append(key, value);
        }
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          form_data.append(`${key}[]`, v);
        });
      } else {
        form_data.append(key, value);
      }
    });

    return getAxios().put(`${ironsides_url}mobile_apps/${guid}`, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  ////////////////////////
  // Website Quoters
  ////////////////////////
  loadWebsiteQuoter(guid) {
    return getAxios().get(`${ironsides_url}website_quoters/${guid}`);
  },

  loadWebsiteQuoters(id) {
    return getAxios().get(`${ironsides_url}website_quoters?agency_id=${id}`);
  },

  updateWebsiteQuoter(id, payload) {
    if (payload.carrier_ids) {
      payload.carrier_ids = [...new Set(payload.carrier_ids.flat(Infinity))];
    }
    if (payload.product_ids) {
      payload.product_ids = [...new Set(payload.product_ids.flat(Infinity))];
    }
    return getAxios().put(`${ironsides_url}website_quoters/${id}`, payload);
  },

  loadWebsiteQuoterField(id) {
    return getAxios().get(`${ironsides_url}website_quoter_fields/${id}`);
  },

  updateWebsiteQuoterField(id, payload) {
    return getAxios().put(`${ironsides_url}website_quoter_fields/${id}`, payload);
  },

  ////////////////////////
  // External Services (API Tokens)
  ////////////////////////
  loadExternalServices() {
    return getAxios().get(`${ironsides_url}api/v1/admin/external-services`);
  },

  createExternalService(payload) {
    return getAxios().post(`${ironsides_url}api/v1/admin/external-services`, payload);
  },

  updateExternalService(id, payload) {
    return getAxios().put(`${ironsides_url}api/v1/admin/external-services/${id}`, payload);
  },

  revokeExternalService(id) {
    return getAxios().post(`${ironsides_url}api/v1/admin/external-services/${id}/revoke`);
  },

  regenerateExternalServiceKey(id) {
    return getAxios().post(`${ironsides_url}api/v1/admin/external-services/${id}/regenerate`);
  },

  deleteExternalService(id) {
    return getAxios().delete(`${ironsides_url}api/v1/admin/external-services/${id}`);
  },

  ////////////////////////
  // Custom Actions
  ////////////////////////
  loadWebsiteQuoterCustomActions(website_quoter_guid) {
    return getAxios().get(
      `${ironsides_url}website_quoter_custom_actions?website_quoter_guid=${website_quoter_guid}`
    );
  },

  loadWebsiteQuoterCustomAction(id) {
    return getAxios().get(`${ironsides_url}website_quoter_custom_actions/${id}`);
  },

  saveWebsiteQuoterCustomAction(guid, payload) {
    if (guid) {
      return getAxios().put(`${ironsides_url}website_quoter_custom_actions/${guid}`, payload);
    } else {
      return getAxios().post(`${ironsides_url}website_quoter_custom_actions`, payload);
    }
  },

  deleteWebsiteCustomAction(id) {
    return getAxios().delete(`${ironsides_url}website_quoter_custom_actions/${id}`);
  }
};

export default Api;
